import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  clearStoredActiveCheckinSession,
  getStoredActiveCheckinSession,
  type StoredActiveCheckinSession,
  setStoredActiveCheckinSession,
} from '@/lib/ba/activeShiftStorage';

const supabaseAny = supabase as any;

const rpcExistsError = (error: unknown) => {
  const message = String((error as { message?: string } | null)?.message ?? '');
  return message.includes('Could not find the function') || message.includes('schema cache');
};

const callFirstAvailableRpc = async <TData,>(
  rpcCalls: Array<{ name: string; args: Record<string, unknown> }>,
) => {
  let lastError: unknown = null;

  for (const rpcCall of rpcCalls) {
    const result = await supabaseAny.rpc(rpcCall.name, rpcCall.args);
    if (!result.error) return result as { data: TData; error: null };
    lastError = result.error;

    if (!rpcExistsError(result.error)) {
      throw result.error;
    }
  }

  throw lastError ?? new Error('RPC unavailable.');
};

const toNullableNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeServerSession = (row: any): StoredActiveCheckinSession => ({
  id: row.id,
  assignmentId: row.assignment_id,
  campaignId: row.campaign_id,
  shiftId: row.shift_id,
  campaignStoreId: row.campaign_store_id ?? null,
  storeId: row.store_id ?? null,
  baId: row.ba_id,
  promoterUserId: row.promoter_user_id,
  checkedInAt: row.checked_in_at,
  checkedOutAt: row.checked_out_at ?? null,
  status: row.status ?? 'active',
  deviceId: row.device_id ?? null,
  campaignName: row.campaign_name ?? 'Campaign',
  shiftRole: row.shift_role ?? null,
  shiftStartsAt: row.shift_starts_at ?? null,
  shiftEndsAt: row.shift_ends_at ?? null,
  storeName: row.store_name ?? null,
  storeAddress: row.store_address ?? null,
  storeLat: toNullableNumber(row.store_lat),
  storeLng: toNullableNumber(row.store_lng),
  geoRadiusMeters: toNullableNumber(row.geo_radius_meters),
  source: 'server',
});

export const useActiveCheckinSession = (options?: { enabled?: boolean }) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['ba-active-checkin-session', user?.id],
    queryFn: async (): Promise<StoredActiveCheckinSession | null> => {
      if (!user) return null;

      try {
        const { data } = await callFirstAvailableRpc<any>([
          {
            name: 'get_active_checkin_session_context_v3',
            args: { p_user_id: user.id },
          },
          {
            name: 'get_active_checkin_session_context_v2',
            args: { p_user_id: user.id },
          },
          {
            name: 'get_active_checkin_session_context',
            args: { p_user_id: user.id },
          },
        ]);

        const row = Array.isArray(data) ? data[0] ?? null : data ?? null;
        if (!row?.id) {
          clearStoredActiveCheckinSession();
          return null;
        }

        const normalized = normalizeServerSession(row);
        setStoredActiveCheckinSession(normalized);
        return normalized;
      } catch (error) {
        console.warn('[ba-active-checkin-session] falling back to local session state', error);
        return getStoredActiveCheckinSession();
      }
    },
    enabled: (options?.enabled ?? true) && !!user,
    refetchInterval: 30000,
  });
};
